import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { data, portal, type ClientOrg } from "@aawaz/api-client";
import { t } from "@aawaz/i18n";
import {
  Badge, Button, Card, ChannelLogo, CopyButton, EmptyState, ErrorBanner, Field, Icon, Input, KV, Modal, Notice, PageHeader, Ring, SkeletonRows, Table,
  Tabs, fmtPeriod, humanize, initials,
} from "@aawaz/ui";
import { renderSVG } from "uqr";
import { useSession } from "../session";
import { ClientDesk } from "./GstDesk";
import { inr, lastMonth } from "./Home";

interface JoinLink { code: string; number: string | null; message: string; link: string | null }
interface DeskRow { client_org_id: string; score: number; itc: { at_risk: string; has_2b: boolean }; open: { text: string }[] }

/** Clients: find one by name, GSTIN or PAN, and see at a glance how ready last month's return is. */
export function ClientsPage() {
  const s = useSession();
  const nav = useNavigate();
  const qc = useQueryClient();
  const period = lastMonth();
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ external_ref: "", display_name: "", pan: "", gstins: "", phones: "" });
  const list = useQuery({ queryKey: ["clients", q], queryFn: () =>
    data<{ data: ClientOrg[] }>(portal.GET("/portal/v1/clients", { params: { query: q ? { q } : {} } })) });
  const desk = useQuery({ queryKey: ["ca-desk", period], queryFn: () =>
    data<{ data: DeskRow[] }>(portal.GET("/portal/v1/ca/desk", { params: { query: { period } } })) });
  const create = useMutation({
    mutationFn: () => data<{ id: string }>(portal.POST("/portal/v1/clients", { body: {
      external_ref: form.external_ref.trim() || null,
      display_name: form.display_name, pan: form.pan || null,
      gstins: form.gstins.split(/[\s,]+/).filter(Boolean), phones: splitPhones(form.phones) } })),
    onSuccess: async (r) => { await qc.invalidateQueries({ queryKey: ["clients"] }); setAdding(false); nav(`/clients/${r.id}`); },
  });
  const byId = new Map((desk.data?.data ?? []).map((r) => [r.client_org_id, r]));
  const rows = list.data?.data ?? [];
  return (
    <>
      <PageHeader title={t("nav.clients")} description={`Readiness is for ${fmtPeriod(period)}'s returns. Open a client for documents, sources and GST.`}
        actions={s.can("manage_clients") ? <Button variant="primary" icon="plus" onClick={() => setAdding(true)}>Add client</Button> : null} />
      <div className="ui-toolbar">
        <div className="ui-search">
          <Icon name="search" size={16} />
          <input aria-label={t("common.search")} placeholder="Search by name, GSTIN or PAN" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <span className="ui-hint">{rows.length} client{rows.length === 1 ? "" : "s"}</span>
      </div>
      <Card flush>
        {list.isLoading ? <div style={{ padding: 22 }}><SkeletonRows /></div> : rows.length === 0 ? (
          <EmptyState icon="building" title={q ? "No client matches" : "No clients yet"}
            action={!q && s.can("manage_clients") ? <Button variant="primary" icon="plus" onClick={() => setAdding(true)}>Add your first client</Button> : undefined}>
            {q ? "Try part of the name, a GSTIN or a PAN." : "Add clients with their GSTINs; bills then find the right client on their own."}
          </EmptyState>
        ) : (
          <Table rows={rows} rowKey={(r) => r.id} onRowClick={(r) => nav(`/clients/${r.id}`)} columns={[
            { key: "n", header: "Client", render: (r) => (
              <div className="ui-cell-main"><span className="ui-avatar ui-avatar-soft" aria-hidden="true">{initials(r.display_name)}</span>
                <div style={{ minWidth: 0 }}><span className="ui-cell-title">{r.display_name}</span>
                  <span className="ui-cell-sub ui-mono">{r.gstins[0] ?? r.pan ?? "No GSTIN"}{r.gstins.length > 1 ? ` +${r.gstins.length - 1}` : ""}</span></div></div>) },
            { key: "s", header: "Ready", render: (r) => { const d = byId.get(r.id); return d ? <Ring value={d.score} size={36} stroke={4} /> : <span className="ui-hint">—</span>; } },
            { key: "i", header: "ITC at risk", render: (r) => { const d = byId.get(r.id);
              return !d ? "—" : d.itc.has_2b ? <strong className={Number(d.itc.at_risk) ? "ui-text-red" : undefined}>{inr(d.itc.at_risk)}</strong>
                : <span className="ui-hint">2B not in</span>; } },
            { key: "o", header: "Open", render: (r) => { const d = byId.get(r.id);
              return !d ? "—" : d.open.length ? <span className="ui-hint">{d.open[0]!.text}{d.open.length > 1 ? ` +${d.open.length - 1}` : ""}</span> : <Badge value="ready" tone="green" />; } },
            ...(rows.some((r) => r.status !== "active") ? [{ key: "x", header: "Status", render: (r: ClientOrg) => <Badge value={r.status} /> }] : []),
          ]} />
        )}
      </Card>
      <Modal open={adding} size="sm" title="Add client" icon={<Icon name="building" size={20} />}
             subtitle="Bills that carry one of these GSTINs are filed to this client on their own." onClose={() => setAdding(false)}>
        <ErrorBanner error={create.error} />
        <form onSubmit={(e) => { e.preventDefault(); create.mutate(); }}>
          <Field label="Name">{(id) => <Input id={id} required autoFocus value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} />}</Field>
          <Field label="GSTINs" hint="Comma separated.">{(id) =>
            <Input id={id} className="ui-input ui-mono" value={form.gstins} onChange={(e) => setForm({ ...form, gstins: e.target.value.toUpperCase() })} />}</Field>
          <Field label="WhatsApp numbers" hint={PHONES_HINT}>{(id) =>
            <Input id={id} type="tel" placeholder="98765 43210, 98765 43211" value={form.phones} onChange={(e) => setForm({ ...form, phones: e.target.value })} />}</Field>
          <div className="ui-grid">
            <Field label="PAN">{(id) => <Input id={id} value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })} />}</Field>
            <Field label="Your reference" hint="Optional: the id in your own system. Left blank, one is assigned (C-0001, …)">{(id) => <Input id={id} value={form.external_ref} onChange={(e) => setForm({ ...form, external_ref: e.target.value })} />}</Field>
          </div>
          <div className="ui-dialog-actions">
            <Button variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
            <Button variant="primary" type="submit" busy={create.isPending}>Add client</Button>
          </div>
        </form>
      </Modal>
    </>
  );
}

const PHONES_HINT = "Comma separated. Files these numbers send to the AawazAI number on WhatsApp are filed under this client.";
/** "98765 43210, +91 98765-43211" -> one entry per number; the server puts each in +91… form. */
const splitPhones = (text: string) => text.split(/[,;\n]+/).map((p) => p.trim()).filter(Boolean);

interface Detail {
  client: ClientOrg; sources: { id: string; binding_type: string; external_ref: string; status: string }[];
  documents_by_period: Record<string, Record<string, number>>; assigned_memberships: string[];
}
const SOURCE_KIND: Record<string, string> = {
  whatsapp_group: "whatsapp", whatsapp_sender: "whatsapp", folder: "gdrive", email_alias: "email_inbound", zoho_org: "zoho_books", tally_company: "tally",
};

/** One client: this month's return, where their documents come from, and every month on file. */
export function ClientDetailPage() {
  const { id = "" } = useParams();
  const s = useSession();
  const [tab, setTab] = useState("gst");
  const period = lastMonth();
  const qc = useQueryClient();
  const remind = useMutation({
    mutationFn: () => data(portal.POST("/portal/v1/ca/chase:send", { body: { period, client_org_ids: [id] } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ca-readiness", id, period] }),
  });
  const q = useQuery({ queryKey: ["clients", id, "tree"], queryFn: () =>
    data<Detail>(portal.GET("/portal/v1/clients/{client_id}", { params: { path: { client_id: id } } })) });
  const [edit, setEdit] = useState<{ display_name: string; legal_name: string; pan: string; gstins: string; phones: string } | null>(null);
  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => data(portal.PATCH("/portal/v1/clients/{client_id}",
      { params: { path: { client_id: id } }, body: body as never })),
    onSuccess: async () => { setEdit(null); await qc.invalidateQueries({ queryKey: ["clients"] }); await qc.invalidateQueries({ queryKey: ["bindings"] }); },
  });
  // the link a client taps to tie their own WhatsApp number to this client (no group, no number typed here)
  const join = useMutation({
    mutationFn: (rotate: boolean) => data<JoinLink>(portal.POST("/portal/v1/clients/{client_id}/join-link",
      { params: { path: { client_id: id }, query: { rotate } } })),
  });
  if (q.isLoading) return <SkeletonRows rows={8} />;
  if (!q.data) return <ErrorBanner error={q.error} />;
  const d = q.data;
  const periods = Object.entries(d.documents_by_period).sort().reverse();
  const total = periods.reduce((n, [, types]) => n + Object.values(types).reduce((a, b) => a + b, 0), 0);
  return (
    <>
      <div className="ui-client-head">
        <span className="ui-firm-mark ui-firm-mark-lg" aria-hidden="true">{initials(d.client.display_name)}</span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <Link to="/clients" className="ui-hint">← Clients</Link>
          <h1>{d.client.display_name}</h1>
          <div className="ui-row" style={{ gap: 6 }}>
            {d.client.gstins.map((g) => <span key={g} className="ui-tag ui-mono">{g}</span>)}
            {d.client.pan ? <span className="ui-tag ui-mono">PAN {d.client.pan}</span> : null}
            {d.client.status !== "active" ? <Badge value={d.client.status} /> : null}
          </div>
        </div>
        {s.can("manage_clients") && d.client.status === "active" ? <Button icon="message" onClick={() => join.mutate(false)} busy={join.isPending && !join.data}>WhatsApp link</Button> : null}
        {s.can("manage_clients") ? <Button icon="settings" onClick={() => setEdit({ display_name: d.client.display_name, legal_name: d.client.legal_name ?? "",
          pan: d.client.pan ?? "", gstins: d.client.gstins.join(", "), phones: (d.client.phones ?? []).join(", ") })}>Edit</Button> : null}
        <Link className="ui-btn" to={`/documents?client=${id}`}><Icon name="file" size={16} />Documents · {total}</Link>
      </div>
      {d.client.status !== "active" ? <Notice>This client is switched off: nothing new is filed under it, and its WhatsApp numbers are not linked. Its documents are kept.
        {s.can("manage_clients") ? <> <Button size="sm" busy={save.isPending} onClick={() => save.mutate({ status: "active" })}>Switch back on</Button></> : null}</Notice> : null}
      <Tabs value={tab} onChange={setTab} tabs={[
        { value: "gst", label: `GST · ${fmtPeriod(period)}` }, { value: "docs", label: "Documents by month", count: periods.length },
        { value: "sources", label: "Sources", count: d.sources.length }, { value: "details", label: "Details" },
      ]} />
      <ErrorBanner error={remind.error ?? (edit ? null : save.error)} />
      {tab === "gst" ? <ClientDesk clientId={id} period={period} canAct={s.can("review")} onRemind={() => remind.mutate()} reminding={remind.isPending} /> : null}
      {tab === "docs" ? (
        <Card flush>
          {periods.length === 0 ? <EmptyState icon="file" title="No documents yet">Documents appear here once they are filed for this client.</EmptyState> : (
            <Table rows={periods.map(([p, types]) => ({ p, types }))} rowKey={(r) => r.p} columns={[
              { key: "p", header: "Month", render: (r) => <Link to={`/documents?client=${id}&period=${r.p}`}><strong>{fmtPeriod(r.p)}</strong></Link> },
              { key: "n", header: "Documents", render: (r) => Object.values(r.types).reduce((a, b) => a + b, 0) },
              { key: "t", header: "Types", render: (r) => <div className="ui-row" style={{ gap: 6 }}>{Object.entries(r.types).map(([k, n]) =>
                <span key={k} className="ui-tag">{humanize(k)} · {n}</span>)}</div> },
            ]} />
          )}
        </Card>
      ) : null}
      {tab === "sources" ? (
        <Card flush>
          {d.sources.length === 0 ? (
            <EmptyState icon="plug" title="No sources linked" action={<Link className="ui-btn ui-btn-primary" to="/channels">Open Channels</Link>}>
              Create a WhatsApp group for this client, or link a Drive folder, Zoho organization or Tally company.
            </EmptyState>
          ) : (
            <div className="ui-conn-list">
              {d.sources.map((r) => (
                <div key={r.id} className="ui-conn">
                  <span className="ui-tile-logo"><ChannelLogo kind={SOURCE_KIND[r.binding_type] ?? r.binding_type} size={22} /></span>
                  <div className="ui-conn-main"><strong>{humanize(r.binding_type)}</strong><span className="ui-conn-meta ui-mono">{r.external_ref}</span></div>
                  <Badge value={r.status} />
                </div>
              ))}
            </div>
          )}
        </Card>
      ) : null}
      <Modal open={!!join.data} size="sm" title="Link this client's WhatsApp" icon={<Icon name="message" size={20} />}
             subtitle="Send this to the client. When they tap it and press send, their own number is linked to this client."
             onClose={() => join.reset()}>
        {join.data ? (
          <>
            <ErrorBanner error={join.error} />
            {join.data.link ? (
              <>
                <div className="ui-qr" dangerouslySetInnerHTML={{ __html: renderSVG(join.data.link, { border: 1 }) }} />
                <div className="ui-row" style={{ gap: 8, justifyContent: "center", margin: "12px 0" }}>
                  <CopyButton text={join.data.link} label="Copy link" />
                  <CopyButton text={`${join.data.message}`} label="Copy message" />
                </div>
              </>
            ) : <Notice>The AawazAI number is not set up on this server, so there is no link yet. The client can still send this message to the number by hand.</Notice>}
            <p className="ui-hint" style={{ margin: "0 0 4px" }}>The message they send{join.data.number ? ` to ${join.data.number}` : ""}:</p>
            <p style={{ margin: 0, whiteSpace: "pre-wrap" }}>{join.data.message}</p>
            <div className="ui-dialog-actions">
              <Button variant="ghost" busy={join.isPending} title="Make a new code. The old link stops working; numbers already linked stay linked."
                      onClick={() => confirm("Make a new link? The current one stops working.") && join.mutate(true)}>New link</Button>
              <Button variant="primary" onClick={() => join.reset()}>Done</Button>
            </div>
          </>
        ) : null}
      </Modal>
      {tab === "details" ? (
        <Card>
          <KV items={[["Reference", d.client.external_ref],
            ["Code for staff", <span key="code">Staff start a WhatsApp caption with <span className="ui-mono">#{d.client.external_ref}</span> to file a bill under this client</span>],
            ["Legal name", d.client.legal_name], ["PAN", d.client.pan], ["GSTINs", d.client.gstins.join(", ")],
            ["WhatsApp numbers", d.client.phones?.join(", ")], ["Assigned staff", String(d.assigned_memberships.length || "All staff")]]} />
          {s.can("manage_clients") && d.client.status === "active" ? (
            <div className="ui-dialog-actions">
              <Button variant="danger" busy={save.isPending}
                      title="Stop filing anything new under this client. Its documents are kept and it can be switched back on."
                      onClick={() => save.mutate({ status: "inactive" })}>Switch this client off</Button>
            </div>
          ) : null}
        </Card>
      ) : null}
      <Modal open={!!edit} size="sm" title="Edit client" icon={<Icon name="building" size={20} />} onClose={() => setEdit(null)}>
        {edit ? (
          <form onSubmit={(e) => { e.preventDefault(); save.mutate({ display_name: edit.display_name, legal_name: edit.legal_name || null, pan: edit.pan || null,
            gstins: edit.gstins.split(/[\s,]+/).filter(Boolean), phones: splitPhones(edit.phones) }); }}>
            <ErrorBanner error={save.error} />
            <Field label="Name">{(fid) => <Input id={fid} required value={edit.display_name} onChange={(e) => setEdit({ ...edit, display_name: e.target.value })} />}</Field>
            <Field label="Legal name" hint="Optional: as printed on bills, when it differs.">{(fid) => <Input id={fid} value={edit.legal_name} onChange={(e) => setEdit({ ...edit, legal_name: e.target.value })} />}</Field>
            <Field label="GSTINs" hint="Comma separated. Bills that carry one of these are filed to this client on their own.">{(fid) =>
              <Input id={fid} className="ui-input ui-mono" value={edit.gstins} onChange={(e) => setEdit({ ...edit, gstins: e.target.value.toUpperCase() })} />}</Field>
            <Field label="PAN">{(fid) => <Input id={fid} value={edit.pan} onChange={(e) => setEdit({ ...edit, pan: e.target.value.toUpperCase() })} />}</Field>
            <Field label="WhatsApp numbers" hint={PHONES_HINT}>{(fid) =>
              <Input id={fid} type="tel" value={edit.phones} onChange={(e) => setEdit({ ...edit, phones: e.target.value })} />}</Field>
            <div className="ui-dialog-actions">
              <Button variant="ghost" onClick={() => setEdit(null)}>Cancel</Button>
              <Button variant="primary" type="submit" busy={save.isPending}>Save</Button>
            </div>
          </form>
        ) : null}
      </Modal>
    </>
  );
}
